import { format, parseISO } from 'date-fns';

export const formatDate = (date: string | Date): string => {
    const d = typeof date === 'string' ? parseISO(date) : date;
    return format(d, 'dd-MM-yyyy');
};

export const formatDay = (date: string | Date): string => {
    const d = typeof date === 'string' ? parseISO(date) : date;
    return format(d, 'EEEE');
};

export const formatDateTime = (date: string | Date | number): string => {
    const d = typeof date === 'number' ? new Date(date) : typeof date === 'string' ? parseISO(date) : date;
    return format(d, 'dd-MM-yyyy HH:mm');
};

const pad2 = (n: number) => String(n).padStart(2, '0');

/**
 * `YYYY-MM-DD` for the viewer's LOCAL calendar day. Use this, never
 * `date.toISOString().slice(0, 10)`: that is the UTC day, which in Dhaka (+06)
 * is still yesterday before 06:00 and turns "1 September" into "31 August".
 */
export const toLocalIsoDate = (date: Date = new Date()): string =>
    `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

/** `YYYY-MM-01` of the local month containing `date`. */
export const firstOfMonthLocalIso = (date: Date = new Date()): string =>
    `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-01`;

/** `YYYY-01-01` of the local year containing `date` (report default range start). */
export const firstOfYearLocalIso = (date: Date = new Date()): string =>
    `${date.getFullYear()}-01-01`;

/**
 * Year/month (1-12) whose figures are worth showing by default. In the first
 * `graceDays` days of a month the current month has (almost) no actuals yet, so
 * the previous month is returned instead.
 */
export const defaultReportMonth = (
    date: Date = new Date(),
    graceDays = 7,
): { year: number; month: number; isPrevious: boolean } => {
    if (date.getDate() <= graceDays) {
        const prev = new Date(date.getFullYear(), date.getMonth() - 1, 1);
        return { year: prev.getFullYear(), month: prev.getMonth() + 1, isPrevious: true };
    }
    return { year: date.getFullYear(), month: date.getMonth() + 1, isPrevious: false };
};

/**
 * `YYYY-MM-01` of the fiscal year that contains `date`, for a fiscal year starting in `startMonth` (1-12).
 * January (the default) gives the 1st of January of this year; with July, any date from July onwards gives 1 July of
 * this year and earlier dates give 1 July of last year.
 */
export const currentFiscalYearStartLocalIso = (startMonth: number = 1, date: Date = new Date()): string => {
    const m = startMonth >= 1 && startMonth <= 12 ? startMonth : 1;
    const year = date.getMonth() + 1 >= m ? date.getFullYear() : date.getFullYear() - 1;
    return `${year}-${pad2(m)}-01`;
};

/** `Sep 2026` for an ISO date (the month label of a period that is stored as its first day). */
export const formatMonthYear = (date: string | Date): string => {
    const d = typeof date === 'string' ? parseISO(date) : date;
    return format(d, 'MMM yyyy');
};
